import Decimal from 'decimal.js';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { useFormContext } from '@/components/react-hook-form';
import { StructuredAssetsSubform_ValuationFragment } from '@/modules/entities/StructuredAssetsSubform/graphql/StructuredAssetsSubform.generated';
import { STRUCTURED_ASSETS_SUBFORM_NAMESPACE } from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.types';
import { AssetValuationV2ValuationSource } from '@/types/schema';
import { sumDecimalJS } from '@/utils/decimalJSUtils';

import { FullScreenStructuredAssetsModalForm } from '../FullScreenStructuredAssetsModal.types';
import { getIntegratedAssetsFromValuation } from '../FullScreenStructuredAssetsModal.utils';
import { useValuationIntegrationDetails } from './useValuationLinkDetails';

export function useTotalAssetValueSum(
  valuation: StructuredAssetsSubform_ValuationFragment | null
): Decimal {
  const { control } = useFormContext<FullScreenStructuredAssetsModalForm>();
  const { isIntegratedScenario } = useValuationIntegrationDetails();
  const [formAssetValueSum, allowAdditionalManualAssets] = useWatch({
    control: control,
    name: [
      `${STRUCTURED_ASSETS_SUBFORM_NAMESPACE}._assetsValueSum`,
      'allowAdditionalManualAssets',
    ],
  });

  const totalAssetValueSum = useMemo(() => {
    // this is kind of annoying, but we need to show a live total of the combination of the manual assets
    // in the form and the integrated assets. so, we actually need to calculate the sum of the integrated assets
    // and append it to the sum of the manual assets in the form.
    if (
      isIntegratedScenario &&
      valuation?.valuationSource !== AssetValuationV2ValuationSource.Manual
    ) {
      const integratedAssets = getIntegratedAssetsFromValuation(valuation);
      const integratedAssetsSum = sumDecimalJS(
        integratedAssets.map((a) => a.assetValue?.ownedValue ?? new Decimal(0))
      );
      if (!allowAdditionalManualAssets) {
        return integratedAssetsSum;
      }
      return integratedAssetsSum.plus(formAssetValueSum);
    }
    return formAssetValueSum;
  }, [
    allowAdditionalManualAssets,
    formAssetValueSum,
    isIntegratedScenario,
    valuation,
  ]);

  return totalAssetValueSum;
}
